import { LOG_TYPES } from '../constants/logTypeRegistry';

export const REVIEW_LABEL = 'Needs review';
export const DATE_REVIEW_LABEL = 'Date needs review';
export const TIME_REVIEW_LABEL = 'Time needs review';

const FACT_KEYS = [
  'sleep',
  'food',
  'medication',
  'behavior',
  'mood',
  'activity',
  'health',
  'environment',
  'people',
];

const BUCKET_LABELS = {
  behavior: 'Behavior',
  food: 'Food',
  sleep: 'Sleep',
  medication: 'Medication',
  medication_context: 'Medication context',
  mood: 'Mood',
  activity: 'Activity',
  health: 'Health',
  school: 'School',
  sensory: 'Sensory',
  environment: 'Environment',
  people: 'People',
  intervention: 'Intervention',
  outcome: 'Outcome',
  daily_log: 'Daily Log',
  log: 'Daily Log',
};

const CATEGORY_ALIASES = {
  behavior_response: 'behavior',
  incident: 'behavior',
  medication_dose: 'medication',
  medication_admin: 'medication',
  meds: 'medication',
  medication_change: 'medication_change',
  food_intake: 'food',
  sleep_period: 'sleep',
  health_note: 'health',
  appointment: 'health',
  bathroom: 'bathroom',
  toilet: 'bathroom',
  activity: 'activity',
  mood: 'mood',
  milestone: 'milestone',
  daily_log: 'log',
  dailyLog: 'log',
  routine: 'log',
  observation: 'log',
  general_note: 'log',
  log: 'log',
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const cleanText = (value) => String(value || '').replace(/\s+/g, ' ').trim();

const compactLabel = (value) => cleanText(value).replace(/_/g, ' ');

const titleize = (value) => compactLabel(value)
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatValue = (value) => {
  if (Array.isArray(value)) {
    return value.map(formatValue).filter(Boolean).join(', ');
  }

  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'object') {
    return '';
  }

  return cleanText(value);
};

const firstValue = (...values) => values.map(formatValue).find(Boolean) || '';

const normalizeCategoryKey = (value) => {
  const rawKey = cleanText(value);
  if (!rawKey) {
    return 'log';
  }

  const directKey = CATEGORY_ALIASES[rawKey] || rawKey;
  return LOG_TYPES[directKey] ? directKey : 'log';
};

const getCategoryKeyFromRecord = (record = {}, candidate = {}) => {
  if (record.incidentCategoryId === 'behavior' || record.timelineType === 'incident' || record.entryType === 'incident') {
    return 'behavior';
  }

  return normalizeCategoryKey(
    record.category
    || record.actionType
    || candidate.candidateType
    || candidate.userFacingType
  );
};

const buildDetail = (label, value) => {
  const formattedValue = formatValue(value);
  return formattedValue ? { label, value: formattedValue } : null;
};

const buildBucketGroup = ({ key, label, values = [], role = '', needsReview = false }) => {
  const cleanedValues = asArray(values).map(formatValue).filter(Boolean);
  if (!cleanedValues.length) {
    return null;
  }

  return {
    key: cleanText(key || label || cleanedValues[0]),
    label: cleanText(label) || BUCKET_LABELS[key] || titleize(key || 'Note'),
    role: compactLabel(role),
    needsReview: Boolean(needsReview),
    values: cleanedValues,
  };
};

const uniqueDetails = (details) => {
  const seen = new Set();
  return details.filter(Boolean).filter((detail) => {
    const key = `${detail.label}:${detail.value}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const getRecordText = (record = {}, fallback = '') => firstValue(
  record.whatHappened,
  record.text,
  record.note,
  record.summary,
  record.description,
  record.content,
  record.body,
  record.details,
  record.originalText,
  record.sourceText,
  fallback
);

const getRecordTitle = (record = {}, candidate = {}) => firstValue(
  record.title,
  record.medicationChangeTitle,
  candidate.userFacingType,
  candidate.candidateType,
  'Timeline entry'
);

const getDateLabel = (record = {}) => firstValue(
  record.date,
  record.localDate,
  record.entryDate,
  record.displayDate,
  record.anchorDate,
  record.moodDateKey,
  record.entryDayKey,
  record.incidentData?.incidentDateTime,
  record.actualDateTime,
  record.effectiveFrom
) || DATE_REVIEW_LABEL;

const getTimeLabel = (record = {}) => firstValue(
  record.time,
  record.localTime,
  record.scheduledTime,
  record.takenAt,
  record.startTime,
  record.endTime,
  record.incidentData?.incidentDateTime,
  record.actualDateTime
) || TIME_REVIEW_LABEL;

const buildBehaviorDetails = (record = {}) => uniqueDetails([
  buildDetail('Severity', firstValue(record.severity, record.incidentData?.severity)),
  buildDetail('Trigger', firstValue(record.triggerSummary, record.incidentData?.triggerSummary)),
  buildDetail('Possible trigger', firstValue(record.triggerNotes, record.incidentData?.triggerNotes)),
  buildDetail('Intervention', firstValue(record.remedy, record.incidentData?.remedy)),
  buildDetail('Duration', record.duration),
]);

const buildMedicationDetails = (record = {}) => uniqueDetails([
  buildDetail('Medication', record.medicationName),
  buildDetail('Dose', firstValue(record.actualDose, record.plannedDose, record.dose)),
  buildDetail('Scheduled', record.scheduledTime),
  buildDetail('Taken', record.takenAt),
  buildDetail('Status', record.status),
]);

const buildMedicationChangeDetails = (record = {}) => uniqueDetails([
  buildDetail('Medication', record.medicationName),
  buildDetail('Change', record.medicationChangeType),
  buildDetail('Effective', firstValue(record.medicationChangeEffectiveLabel, record.effectiveFrom, record.actualDateTime)),
  buildDetail('Summary', firstValue(record.medicationChangeSummary, record.summary)),
]);

const buildRelatedMedicationDetails = (record = {}) => {
  const context = record.relatedMedicationContext || record.metadata?.relatedMedicationContext || {};
  return uniqueDetails([
    buildDetail('Medication context', firstValue(context.relationship, context.changeMentioned)),
    buildDetail('Medication mentioned', context.medicationName),
    buildDetail('Context certainty', context.certainty),
  ]);
};

const buildFoodDetails = (record = {}) => {
  const details = record.foodDetails || {};
  return uniqueDetails([
    buildDetail('Meal', details.mealType || record.mealType),
    buildDetail('Food', details.whatWasEaten),
    buildDetail('Amount', details.portion),
    buildDetail('Reaction', details.reaction),
    ...buildRelatedMedicationDetails(record),
  ]);
};

const buildSleepDetails = (record = {}) => {
  const details = record.sleepDetails || {};
  return uniqueDetails([
    buildDetail('Wake', details.wakeTime),
    buildDetail('Start', firstValue(record.startTime, details.startTime)),
    buildDetail('End', firstValue(record.endTime, details.endTime)),
    buildDetail('Quality', details.quality),
    buildDetail('Disturbance', details.disturbances),
    buildDetail('Duration', details.duration),
  ]);
};

const buildActivityDetails = (record = {}) => {
  const details = record.activityDetails || {};
  return uniqueDetails([
    buildDetail('Activity', firstValue(details.activityThemeLabel, details.activityType, details.activityTypes)),
    buildDetail('Engagement', firstValue(details.engagementLabel, details.engagement)),
    buildDetail('Duration', details.activityTiming?.durationMinutes ? `${details.activityTiming.durationMinutes} min` : ''),
  ]);
};

const buildBathroomDetails = (record = {}) => {
  const details = record.bathroomDetails || {};
  return uniqueDetails([
    buildDetail('Type', details.type),
    buildDetail('Location', details.location),
    buildDetail('Consistency', firstValue(details.consistencyLabel, details.consistency)),
  ]);
};

const buildRecordDetails = (categoryKey, record = {}, candidate = {}) => {
  if (candidate.candidateType === 'medication_change' || categoryKey === 'medication_change') {
    return buildMedicationChangeDetails(record);
  }

  const detailsByCategory = {
    behavior: buildBehaviorDetails,
    medication: buildMedicationDetails,
    food: buildFoodDetails,
    sleep: buildSleepDetails,
    activity: buildActivityDetails,
    bathroom: buildBathroomDetails,
    mood: (entry) => uniqueDetails([buildDetail('Mood', firstValue(entry.moodValue, entry.value, entry.data?.value))]),
  };

  const categoryDetails = detailsByCategory[categoryKey]?.(record) || [];
  return uniqueDetails([
    ...categoryDetails,
    ...(categoryKey === 'food' ? [] : buildRelatedMedicationDetails(record)),
    buildDetail('Type', compactLabel(candidate.candidateType || record.recordType)),
    buildDetail('Time confidence', record.timeConfidence),
  ]);
};

const getFactGroups = (facts = {}) => FACT_KEYS.map((key) => ({
  key,
  label: BUCKET_LABELS[key] || titleize(key),
  values: asArray(facts[key]).map(formatValue).filter(Boolean),
})).filter((group) => group.values.length);

const getStoryBucketGroups = (record = {}) => {
  const storyBuckets = record.metadata?.storyBuckets || record.storyBuckets || [];
  return asArray(storyBuckets)
    .map((bucket, index) => buildBucketGroup({
      key: bucket.bucket || bucket.type || `bucket-${index}`,
      label: bucket.label,
      role: bucket.role,
      needsReview: bucket.needsReview,
      values: [
        bucket.summary,
        bucket.sourceText && bucket.sourceText !== bucket.summary ? bucket.sourceText : '',
      ],
    }))
    .filter(Boolean);
};

const buildLocalItem = (record = {}, index) => {
  const categoryKey = normalizeCategoryKey(record.userFacingCategory?.category || record.recordType);
  return {
    id: `local-${index}`,
    categoryKey,
    title: getRecordTitle(record),
    body: getRecordText(record),
    dateLabel: getDateLabel(record),
    timeLabel: getTimeLabel(record),
    confidence: record.confidence,
    needsReview: Boolean(record.needsReview),
    sourceLabel: 'Local parser',
    details: buildRecordDetails(categoryKey, record, { candidateType: record.recordType }),
    factGroups: getFactGroups(record.facts),
    bucketGroups: getFactGroups(record.facts),
  };
};

const buildAiItem = (candidate = {}, index) => {
  const record = candidate.record || {};
  const categoryKey = getCategoryKeyFromRecord(record, candidate);
  return {
    id: `ai-${index}`,
    categoryKey,
    title: getRecordTitle(record, candidate),
    body: getRecordText(record, candidate.sourceText),
    dateLabel: getDateLabel(record),
    timeLabel: getTimeLabel(record),
    confidence: candidate.confidence || record.extractionConfidence,
    needsReview: Boolean(candidate.needsReview || record.needsReview),
    sourceLabel: 'Backend AI',
    details: buildRecordDetails(categoryKey, record, candidate),
    factGroups: [],
    bucketGroups: getStoryBucketGroups(record),
  };
};

export const buildRawCaptureTimelinePreviewItems = ({ records, candidates } = {}) => (
  candidates ? asArray(candidates).map(buildAiItem) : asArray(records).map(buildLocalItem)
);

export const groupRawCaptureTimelinePreviewItemsByDate = (items) => items.reduce((groups, item) => {
  const key = item.dateLabel || DATE_REVIEW_LABEL;
  if (!groups[key]) {
    groups[key] = [];
  }
  groups[key].push(item);
  return groups;
}, {});
